import ProductImage from './ProductImage';
import React, { useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useProductCard } from '../hooks/useProductCard';
import useDialogFocus from '../hooks/useDialogFocus';

const ProductCardDesktop = ({ product }) => {
  const {
    notice,
    currentIndex,
    setCurrentIndex,
    fullUrls,
    modalOpen,
    variants,
    selectedVariant,
    setSelectedVariant,
    loadingVariants,
    addToCart,
    closeModal,
    handleModalAdd,
    toggleFavorite,
    favorite,
    displayPrice,
    oldPrice,
    discountPercent,
  } = useProductCard(product);
  const dialogRef = useDialogFocus(modalOpen, closeModal);

  const [isHovering, setIsHovering] = React.useState(false);
  const intervalRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    if (
      isHovering &&
      fullUrls.length > 1 &&
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      intervalRef.current = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % fullUrls.length);
      }, 1900);
    } else {
      clearInterval(intervalRef.current);
    }
    return () => clearInterval(intervalRef.current);
  }, [isHovering, fullUrls.length, setCurrentIndex]);

  const goTo = (index, e) => {
    e.stopPropagation();
    e.preventDefault();
    setCurrentIndex(index);
  };

  const nextImage = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setCurrentIndex((prev) => (prev + 1) % fullUrls.length);
  };

  const prevImage = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setCurrentIndex((prev) => (prev - 1 + fullUrls.length) % fullUrls.length);
  };

  const handleClick = () => {
    sessionStorage.setItem(
      'catalogScroll',
      JSON.stringify({
        url: location.pathname + location.search,
        scroll: window.scrollY,
      })
    );
  };

  return (
    <>
      <article
        className="product-card"
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => {
          setIsHovering(false);
          setCurrentIndex(0);
        }}
      >
        <button
          onClick={toggleFavorite}
          className={`favorite-btn ${favorite ? 'active' : ''}`}
          aria-label={
            favorite ? 'Убрать из избранного' : 'Добавить в избранное'
          }
          type="button"
        >
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill={favorite ? 'currentColor' : 'none'}
            stroke="currentColor"
            strokeWidth="2"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
            />
          </svg>
        </button>

        <div className="product-card-img-wrapper">
          <Link
            to={`/product/${product.id}`}
            onClick={handleClick}
            aria-label={product.title}
          >
            <ProductImage product={product} src={fullUrls[currentIndex]} width="100%" height="100%" alt={product.title} style={{ objectFit: 'cover' }} />
          </Link>
          {fullUrls.length > 1 && (
            <>
              <button
                aria-label="Предыдущее фото"
                className="arrow left"
                onClick={prevImage}
              >
                ‹
              </button>
              <button
                aria-label="Следующее фото"
                className="arrow right"
                onClick={nextImage}
              >
                ›
              </button>
              <div className="dots">
                {fullUrls.map((_, idx) => (
                  <button
                    key={idx}
                    aria-label={`Фото ${idx + 1}`}
                    className={`dot ${idx === currentIndex ? 'active' : ''}`}
                    onClick={(e) => goTo(idx, e)}
                  />
                ))}
              </div>
            </>
          )}
          {/* Блок склеек удалён с витрины */}
        </div>

        <div className="product-card-body">
          <div className="price-block">
            {oldPrice && <span className="old-price">{oldPrice} ₽</span>}
            <span className="product-card-price">{displayPrice} ₽</span>
            {discountPercent > 0 && (
              <span className="discount-badge">-{discountPercent}%</span>
            )}
          </div>
          <h3 className="product-card-title">
            <Link to={`/product/${product.id}`} onClick={handleClick}>
              {product.title}
            </Link>
          </h3>
          {notice && <p className="purchase-notice" role="status">{notice}</p>}
          <div className="product-card-actions">
            <button className="btn btn-primary" onClick={addToCart}>
              Выбрать размер
            </button>
          </div>
        </div>
      </article>

      {modalOpen && (
        <div className="modal-overlay">
          <button
            className="modal-backdrop"
            aria-label="Закрыть выбор размера"
            tabIndex={-1}
            onClick={closeModal}
          />
          <div
            ref={dialogRef}
            className="modal-content"
            role="dialog"
            aria-modal="true"
            aria-label="Выберите размер товара"
            tabIndex={-1}
          >
            <button
              className="modal-close"
              onClick={closeModal}
              aria-label="Закрыть выбор размера"
            >
              ✕
            </button>
            <h3>Выберите размер</h3>
            {loadingVariants ? (
              <p>Загрузка размеров...</p>
            ) : (
              <>
                <div className="variant-list">
                  {variants.map((v) => {
                    const isAvailable = v.stock > 0;
                    const isSelected = selectedVariant?.id === v.id;
                    const variantDiscount = oldPrice
                      ? Math.round((1 - (v.retailPrice ?? 0) / oldPrice) * 100)
                      : null;
                    return (
                      <button
                        key={v.id}
                        className={`variant-btn ${isSelected ? 'selected' : ''} ${!isAvailable ? 'disabled' : ''}`}
                        disabled={!isAvailable}
                        onClick={() => setSelectedVariant(v)}
                      >
                        <span>{v.size}</span>
                        <span className="variant-price">
                          {v.retailPrice ?? ''} ₽
                        </span>
                        {variantDiscount > 0 && (
                          <span className="variant-discount">
                            -{variantDiscount}%
                          </span>
                        )}
                        {!isAvailable && (
                          <span className="out-of-stock">(нет)</span>
                        )}
                      </button>
                    );
                  })}
                </div>
                <button
                  className="btn btn-primary"
                  style={{ width: '100%', marginTop: 15 }}
                  disabled={!selectedVariant || selectedVariant.stock === 0}
                  onClick={handleModalAdd}
                >
                  В корзину
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ProductCardDesktop;
